import { useState } from 'react';
import { Check } from 'lucide-react';
import { FREE_EXPORTS } from '@nextcollage/engine';
import { CHECKOUT_URL, PRICE, useLicense } from '@/state/license';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';

const INCLUDED = [
  'Unlimited full-resolution exports',
  'Collage, grid splitter and carousel',
  'The same purchase works on iPhone and Android',
  'One payment — no subscription, no account',
];

/**
 * The one place the app asks for money.
 *
 * It offers two doors, because a person who lands here has already paid about
 * half the time: they bought it on their phone and are now on a laptop. Making
 * them hunt for where to put a code they already hold would be the worst
 * possible first impression of a paid product.
 */
export function Paywall() {
  const open = useLicense((s) => s.paywallOpen);
  const setPaywall = useLicense((s) => s.setPaywall);
  const redeem = useLicense((s) => s.redeem);

  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    setError(redeem(code) ? null : 'That code was not recognised. Check that it copied in full.');
  };

  return (
    <Modal
      open={open}
      onClose={() => setPaywall(false)}
      title="Unlock NextCollage"
      subtitle={`The first ${FREE_EXPORTS} exports are free. After that it is ${PRICE}, once.`}
      width={560}
    >
      <div className="flex flex-col gap-5 px-4 py-4 sm:px-5 sm:py-5">
        <ul className="flex flex-col gap-2">
          {INCLUDED.map((line) => (
            <li key={line} className="flex items-start gap-2 text-[13px]">
              <Check size={14} className="mt-[3px] shrink-0 text-[var(--accent-solid)]" />
              <span>{line}</span>
            </li>
          ))}
        </ul>

        <Button
          variant="primary"
          className="h-10 w-full text-[14px]"
          onClick={() => {
            window.location.href = CHECKOUT_URL;
          }}
        >
          Unlock everything — {PRICE}
        </Button>

        <div className="flex items-center gap-3">
          <span className="h-px flex-1 bg-[var(--hairline)]" />
          <span className="text-[11px] uppercase tracking-wide text-[var(--text-tertiary)]">
            Already bought it
          </span>
          <span className="h-px flex-1 bg-[var(--hairline)]" />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="unlock-code" className="text-[12px] text-[var(--text-secondary)]">
            Paste your unlock code. If you bought it in the app, open it and tap{' '}
            <span className="text-[var(--text-primary)]">Unlock → Use on the web</span>.
          </label>
          <div className="flex gap-2">
            <input
              id="unlock-code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
              placeholder="NC1.…"
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
              className="h-8 min-w-0 flex-1 rounded-[8px] bg-[var(--bg-sunken)] px-2.5 font-mono text-[12px] shadow-[inset_0_0_0_1px_var(--hairline)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
            />
            <Button onClick={submit} disabled={!code.trim()}>
              Unlock
            </Button>
          </div>
          {error && <p className="text-[12px] text-[var(--danger)]">{error}</p>}
        </div>
      </div>
    </Modal>
  );
}
